import {
  DayOfWeek,
  LocalBusiness,
  Offer,
  OpeningHoursSpecification,
  Service,
  WithContext,
} from "schema-dts";

import { EQUIPMENTSHARE_BASE_URL } from "../constants";

import { LocationJson } from "./types";

import { isTruthy, stripHtmlTags } from "~/utils";

export const LOCATION_DIRECTORY_PATH_PATTERN =
  /^\/location-directory\/([^/]+)\/([^/]+)\/([^/]+)/i;

/**
 * Resolve the Webflow "locations" collection item slug for a location-directory
 * page.
 *
 * The canonical CMS slug encodes multi-word city names with a DOUBLE hyphen
 * (space -> "--"), e.g. "mo-cape--girardeau-equipment-rental", because single
 * hyphens delimit the state / city / branch segments. The page URL uses single
 * hyphens ("/mo/cape-girardeau/..."), so rebuilding the slug from the path is
 * lossy for multi-word cities and requests a key that does not exist (the
 * static JSON then 403s). Prefer the canonical slug Webflow binds onto the page
 * (`[data-wf-item-slug]`); only fall back to the path when it is absent.
 *
 * Returns `undefined` for paths that are not location-directory pages.
 *
 * NOTE: the "--" convention is also encoded in the inverse direction by
 * `getRelativePathFromLocationSlug` in src/Atom/LocationCard/utils.ts (slug ->
 * relative path) and by the omnichannel-api file generator. Keep them in sync;
 * a shared location-slug module would be the eventual home for this rule.
 */
export const resolveLocationItemSlug = (
  pathname: string,
  canonicalSlug?: string | null
): string | undefined => {
  const matches = pathname.match(LOCATION_DIRECTORY_PATH_PATTERN);

  if (!matches) {
    return undefined;
  }

  const trimmed = canonicalSlug?.trim();
  if (trimmed) {
    return trimmed;
  }

  return `${matches[1]}-${matches[2]}-${matches[3]}`;
};

export const makeHoursObject = (
  dayOfWeek: DayOfWeek,
  hours: string
): OpeningHoursSpecification | undefined => {
  if (/^closed$/i.test(hours.trim())) {
    return;
  }

  const terms = hours.split(" - ");

  return {
    "@type": "OpeningHoursSpecification",
    opens: convertTime(terms[0])!,
    closes: convertTime(terms[1])!,
    dayOfWeek: dayOfWeek,
  };
};

export const convertTime = (timeString: string) => {
  const regex = /^(\d+):(\d+)(AM|PM)$/i;
  const timeTerms = timeString.match(regex);
  if (timeTerms?.length !== 4) {
    throw new Error(
      `${timeString} timeString is not properly formatted for json+ld!`
    );
  }

  const hr = timeTerms[1].trim();
  const min = timeTerms[2].trim();
  const period = timeTerms[3].trim().toUpperCase();
  const hrNum = Number(hr);
  const minutes = min.padStart(2, "0");

  let hours24: number;
  if (period === "AM") {
    hours24 = hrNum % 12; // 12AM -> 0
  } else {
    hours24 = (hrNum % 12) + 12; // 12PM -> 12
  }

  const hoursStr = String(hours24).padStart(2, "0");

  return `${hoursStr}:${minutes}`;
};

export const openingHoursMap = (
  data: LocationJson
): Array<OpeningHoursSpecification> => {
  return (
    [
      ["Monday", data.hours_of_operation_monday],
      ["Tuesday", data.hours_of_operation_tuesday],
      ["Wednesday", data.hours_of_operation_wednesday],
      ["Thursday", data.hours_of_operation_thursday],
      ["Friday", data.hours_of_operation_friday],
      ["Saturday", data.hours_of_operation_saturday],
      ["Sunday", data.hours_of_operation_sunday],
    ] as Array<[DayOfWeek, string]>
  )
    .map((day) => makeHoursObject(...day))
    .filter(isTruthy);
};

export const buildStructuredData = (
  location: LocationJson
): WithContext<LocalBusiness> => {
  const itemListElement: Array<Offer> = [];

  location.markets.forEach((market) =>
    market.rental_services.forEach((service) => {
      if (
        itemListElement.some(
          (el) => (el.itemOffered as Service)?.name === service.name
        )
      ) {
        return;
      }

      itemListElement.push({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: service.name,
          description: stripHtmlTags(service.rich_text_content),
        },
      });
    })
  );

  const structuredData: WithContext<LocalBusiness> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `EquipmentShare ${location.name}`,
    description: stripHtmlTags(location.description),
    branchCode: String(
      location.markets.reduce((prev, curr) =>
        (prev.solution.at(0)?.slug ?? "") < (curr.solution.at(0)?.slug ?? "")
          ? prev
          : curr
      )?.slug || ""
    ),
    url: `${EQUIPMENTSHARE_BASE_URL}/locations/${location.slug}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: location.location_address,
      addressLocality: location.city,
      addressRegion: location.region_state_abbreviation,
      postalCode: location.postal_code,
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: location.latitude,
      longitude: location.longitude,
    },
    telephone: location.location_phone,
    openingHoursSpecification: openingHoursMap(location),
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: location.latitude,
        longitude: location.longitude,
      },
      geoRadius: Number(location.delivery_radius_miles),
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Rental Services",
      itemListElement,
    },
  };

  return structuredData;
};
